import React, { useEffect, useRef } from "react";

import Image from "../../components/image";
import ProjectOverview from "../../components/location-page/projectOverview";
import ServiceInfo from "../../components/location-page/serviceInfo";
import FocusArticles from "../../components/program-focus/focusArticles";
import FocusGviExperiences from "../../components/program-focus/focusGviExperiences";
import ProgramFocusHeadInfo from "../../components/program-focus/focusHeadInfo";
import EnquireCard from "../../components/shared/enquireCard";
import ProgramsFiltered from "../../container/location-page/programsFiltered";
import { useBannerDetails } from "../../hooks/useBannerManager";
import HeadLightBgImage from "../../images/location-head-map-light.png";
import HeadDarkBgImage from "../../images/location-head-map.png";

const LocationPageComponent = ({
  page,
  genericDetails,
  groupProgramSourceType,
  customPrograms,
  pageProgramFilters,
  theme
}) => {
  const containerRef = useRef(null);
  const stickyRef = useRef(null);

  const { colorMode, localeLanguage } = theme ?? {};

  useEffect(() => {
    const handleScroll = () => {
      const toppoint = containerRef.current?.getBoundingClientRect().top;
      const bottompoint = containerRef.current?.getBoundingClientRect().bottom;
      const cardTop = (window.innerHeight - 410) / 2;
      const cardHeight = 410;
      if (stickyRef.current) {
        if (toppoint > cardTop) {
          stickyRef.current.style.top = 0;
          stickyRef.current.style.bottom = "auto";
        } else if (bottompoint < cardHeight + cardTop) {
          stickyRef.current.style.top = "auto";
          stickyRef.current.style.bottom = 0;
        } else {
          stickyRef.current.style.top = cardTop - toppoint + "px";
          stickyRef.current.style.bottom = "auto";
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [stickyRef, containerRef]);

  const headTitle = page.title ?? "";
  const acfFields = page?.acfLandingPage ?? page?.acfLandingPage;
  const imageGallery =
    (acfFields?.genFeaturedGallery &&
      acfFields.genFeaturedGallery.map((imgItem) => ({
        ...imgItem,
        type: "image"
      }))) ??
    [];
  const headSubtitle =
    acfFields && acfFields?.genSubtitle ? acfFields.genSubtitle : "";
  const secondayHeadline =
    acfFields && acfFields?.genSecondaryHeadline
      ? acfFields.genSecondaryHeadline
      : "";
  const programFeaturedVideo = "";
  const genFeaturedVideos = acfFields?.genFeaturedVideos ?? [];
  const programUri = "";
  const gotoViewText = "See gallery";
  const locationSummary = page.content ?? "";
  const tabDetails = acfFields?.genContentTabs?.filter((tab) => tab) ?? [];
  const keyMessageIcons =
    genericDetails?.globalProgramConfig?.acfKeyMessagingIcons ?? [];
  const keyMessagingSet = acfFields?.genKeyMessagingIconset ?? "locations";

  const locationBlogPosts =
    acfFields?.genProgramLocation?.flatMap(
      (location) => location?.posts?.nodes
    ) ?? [];

  const blogPosts =
    locationBlogPosts && locationBlogPosts.length > 0 ? locationBlogPosts : [];

  let programTypeFilter = pageProgramFilters?.getFilterByProgramType ?? [];
  let filterByProgramType =
    programTypeFilter && programTypeFilter?.length > 0
      ? `?program_type=${programTypeFilter}`
      : "";
  let applyNow = `/application-form/${filterByProgramType}`;
  const enquireNow = "/enquiry-form/";

  const mapIllustration = (
    <Image
      imageAlt="Background map"
      image={""}
      imageClass="absolute -bottom-[40vw] right-0 z-0 w-full h-auto sm:-bottom-[7vw] opacity-10 planet:opacity-100"
      sourceUrl={
        acfFields?.genMapIcon?.sourceUrl ??
        (colorMode === "light" ? HeadLightBgImage : HeadDarkBgImage)
      }
    />
  );

  const getExperienceLocationPages =
    acfFields?.genProgramLocation?.flatMap(
      (loc) => loc?.locationPages?.nodes
    ) ?? [];

  const locationPageFilterSlugs =
    getExperienceLocationPages?.map((loc) => loc.slug) ?? [];

  const { stickyTitle, stickyImage, stickyDesc } =
    genericDetails?.globalProgramConfig?.acfGenericGlobalConfig ?? {};

  const banner = useBannerDetails(page?.slug);

  const enquireCardDetails = {
    cardTitle: null,
    enquireCardImg: banner?.image || stickyImage,
    cardDesc:
      `<div>${banner?.title || stickyTitle || ""}</div><div class='font-normal font-body text-sm pt-4'>${banner?.desc || stickyDesc || ""}</div>` ??
      "",
    cardBookDesc: null, //stickyBookDesc ?? "Book by"
    shareButton: false,
    language: localeLanguage
  };

  return (
    <>
      <ProgramFocusHeadInfo
        headTitle={headTitle}
        headSubtitle={headSubtitle}
        programFeaturedVideo={programFeaturedVideo}
        programUri={programUri}
        gallery={imageGallery}
        gotoViewText={gotoViewText}
        locationSummary={locationSummary}
        featureMapIllustration={mapIllustration}
        genFeaturedVideos={genFeaturedVideos}
        viewProgramCTAPreset={"secondary"}
        ctaIconClass={
          "group-hover:fill-white fill-black-lighter stroke-transparent group-hover:stroke-transparent planet:fill-white planet:group-hover:fill-gray-darker"
        }
      />
      <div className="relative sticky-wrapper">
        <ServiceInfo keyIcons={keyMessageIcons} iconset={keyMessagingSet} />
        <ProjectOverview
          subtitle={secondayHeadline}
          tabsDetails={tabDetails}
          locationData={acfFields?.genProgramLocation}
          locationPageSlugs={locationPageFilterSlugs}
        />
        <div className="w-full hidden md:flex md:absolute h-[410px] md:h-full md:sticky-position md:!top-8 md:w-auto md:!right-[9.5%]">
          <div
            className="relative w-11/12 md:w-[355px] mx-auto md:m-0"
            ref={containerRef}
          >
            <div ref={stickyRef} className={`card-sticky-container absolute`}>
              <EnquireCard
                applyNow={applyNow}
                enquireNow={enquireNow}
                enquireCardDetails={enquireCardDetails}
              />
            </div>
          </div>
        </div>
      </div>
      <ProgramsFiltered
        locationData={acfFields?.genProgramLocation}
        showCommunityPrograms={!!acfFields?.genHasCommunityPrograms}
        customPrograms={customPrograms}
        pageProgramFilters={pageProgramFilters}
        groupProgramSourceType={groupProgramSourceType}
      />
      <FocusGviExperiences locationPageSlugs={locationPageFilterSlugs} />
      <FocusArticles blogPosts={blogPosts} />
    </>
  );
};

export default LocationPageComponent;
